import { useState } from "react";
import clsx from "clsx";
import dayjs from "dayjs";
import { PortalDialogProps } from "@app/services/modal.service";
import Modal from "../modal/modal.component";
import { Account, ImageCloud } from "@app/types";
import { formatMoneyVN, getCloudExpiry } from "@app/utils/util";
import { useT } from "@app/contexts/preferences.context";
import { IcClock, IcCloud, IcMinus, IcPlus, IcWallet, IcX } from "../icons";
import { btn, btnLg, btnPrimary, iconBtn } from "../ui";

interface ExtendCloudModalProps {
  image: ImageCloud;
  currentAccount?: Account;
}

/** Phần cứng thêm đã được admin duyệt thì gia hạn cùng (accepted_add_hardwares === 3, như API extendsImageVM) */
const HARDWARE_ACCEPTED = 3;
/** Số lần gia hạn tối đa trong 1 lượt, khớp MAX_EXTEND_TIMES của API */
const MAX_TIMES = 6;

/**
 * Gia hạn Image Cloud theo bản thiết kế: chọn 1–6 lần gói (mỗi lần + giờ chơi, + ngày sử dụng).
 * Số tiền tính y như API extendsImageVM: (giá gói - chiết khấu discount_cloud + phần cứng thêm) × số lần.
 * Trả về { times } để trang gọi API, hoặc { topup: số tiền thiếu } để mở Nạp tiền.
 */
function ExtendCloudModal({ portalDialogRef, portalData }: PortalDialogProps<ExtendCloudModalProps>) {
  const t = useT();
  const [times, setTimesState] = useState(1);
  const image = portalData?.image;
  const cloud = image?.cloud;

  if (!image || !cloud) return null;

  const setTimes = (n: number) => setTimesState(Math.min(MAX_TIMES, Math.max(1, Math.floor(n) || 1)));
  const account = portalData?.currentAccount;
  const discount = (cloud.price * times * (account?.discount_cloud || 0)) / 100;
  const hardware =
    image.accepted_add_hardwares === HARDWARE_ACCEPTED
      ? (image.hardwares || []).reduce((sum, item) => sum + item.price, 0) * times
      : 0;
  const total = cloud.price * times - discount + hardware;
  const remain = (account?.balance || 0) - total;

  // Giờ chơi: đã hết hạn thì API reset giờ đã dùng và giờ chơi về đúng giờ của gói
  const leftHours = image.has_expired ? 0 : Math.max(0, image.hours_of_use - (image.total_minute_used || 0) / 60);
  const afterHours = image.has_expired ? cloud.hours_of_use * times : leftHours + cloud.hours_of_use * times;
  // Hạn sử dụng = ngày tạo + days_of_use ngày; đã quá hạn thì API tính hạn mới từ lúc gia hạn
  const expiry = getCloudExpiry(image);
  const expired = image.has_expired || expiry.isBefore(dayjs());
  const newExpiry = (expired ? dayjs() : expiry).add((cloud.days_of_use || 0) * times * 24, "hour");
  const hm = (h: number) => `${Math.floor(h)}h${String(Math.round((h % 1) * 60)).padStart(2, "0")}`;

  const close = () => portalDialogRef.close();
  const row = "flex items-center justify-between gap-3";

  return (
    <Modal onCancel={close} buttonCancelInChildren={false}>
      <div className="flex w-[min(520px,calc(100vw-32px))] flex-col text-fg">
        <div className="flex items-start gap-3 border-b border-line px-[18px] pb-3 pt-4">
          <h2 className="flex flex-1 flex-col gap-0.5 text-[17px] font-bold">
            {t("Gia hạn Image")}
            <small className="text-[12.5px] font-medium text-muted">{image.name}</small>
          </h2>
          <button type="button" className={clsx(iconBtn, "!h-8 !w-8 flex-none")} aria-label={t("Đóng")} onClick={close}>
            <IcX className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-col gap-3 px-[18px] pb-[18px] pt-4">
          <div className="flex items-center gap-3 rounded-xl bg-surface-2 px-3 py-2.5">
            <span className="grid h-11 w-11 flex-none place-items-center rounded-[10px] bg-surface text-brand">
              <IcCloud className="h-6 w-6" />
            </span>
            <div className="min-w-0">
              <b className="block text-base">{cloud.name}</b>
              <span className="text-[13px] text-muted">
                {t("Mỗi lần gia hạn")}: +{cloud.hours_of_use} {t("giờ chơi")}, +{cloud.days_of_use} {t("ngày sử dụng")}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13px] font-semibold text-muted">{t("Số lần gia hạn")}</span>
            <div className="inline-flex items-center overflow-hidden rounded-[10px] border border-line">
              <button
                type="button"
                disabled={times <= 1}
                className="grid h-10 w-10 place-items-center text-muted hover:bg-surface-2 disabled:opacity-40"
                onClick={() => setTimes(times - 1)}
              >
                <IcMinus className="h-4 w-4" />
              </button>
              <input
                type="number"
                min={1}
                max={MAX_TIMES}
                value={times}
                onChange={(e) => setTimes(Number(e.target.value))}
                className="h-10 w-12 border-x border-line bg-transparent text-center font-semibold tabular-nums outline-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <button
                type="button"
                disabled={times >= MAX_TIMES}
                className="grid h-10 w-10 place-items-center text-muted hover:bg-surface-2 disabled:opacity-40"
                onClick={() => setTimes(times + 1)}
              >
                <IcPlus className="h-4 w-4" />
              </button>
            </div>
            <div className="ml-auto flex gap-1.5">
              {[1, 2, 3].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setTimes(n)}
                  className={clsx(
                    "min-w-[36px] rounded-lg border px-2 py-1 text-[13px]",
                    times === n ? "border-brand text-brand" : "border-line text-muted"
                  )}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2 rounded-xl bg-surface-2 px-4 py-3 text-sm tabular-nums">
            <div className={row}>
              <span className="text-muted">{t("Giờ chơi còn lại")}</span>
              <b>{hm(leftHours)}</b>
            </div>
            <div className={row}>
              <span className="text-muted">{t("Sau gia hạn")}</span>
              <b className="text-brand">{hm(afterHours)}</b>
            </div>
            <div className={row}>
              <span className="text-muted">{t("Hạn sử dụng hiện tại")}</span>
              <b>{expired ? t("Đã hết hạn") : expiry.format("DD/MM/YYYY HH:mm")}</b>
            </div>
            <div className={row}>
              <span className="text-muted">{t("Hạn sử dụng mới")}</span>
              <b className="text-brand">{newExpiry.format("DD/MM/YYYY HH:mm")}</b>
            </div>
            {hardware > 0 && (
              <div className={row}>
                <span className="text-muted">{t("Phần cứng thêm")}</span>
                <b>{formatMoneyVN(hardware)} VND</b>
              </div>
            )}
            <div className={clsx(row, "border-t border-line pt-2")}>
              <b>{t("Thành tiền")}</b>
              <span className="flex items-baseline gap-2">
                {discount > 0 && <s className="text-xs text-faint">{formatMoneyVN(cloud.price * times + hardware)}</s>}
                <strong className="text-xl text-brand">{formatMoneyVN(total)} VND</strong>
              </span>
            </div>
          </div>

          {image.has_expired && image.total_minute_used < image.hours_of_use * 60 && (
            <p className="m-0 text-xs text-muted">
              {t("Giờ chơi chưa dùng của đơn cũ đã hết hiệu lực khi tới hạn nên không được cộng lại.")}
            </p>
          )}

          {account && remain < 0 ? (
            // Thiếu tiền: nút chuyển thành Nạp thêm như bản thiết kế
            <button
              type="button"
              className={clsx(btn, btnPrimary, btnLg, "w-full")}
              onClick={() => portalDialogRef.close({ topup: -remain })}
            >
              <IcWallet className="h-[18px] w-[18px]" />
              {t("Nạp thêm")} {formatMoneyVN(Math.ceil(-remain))} VND
            </button>
          ) : (
            <>
              <button
                type="button"
                className={clsx(btn, btnPrimary, btnLg, "w-full")}
                onClick={() => portalDialogRef.close({ times })}
              >
                <IcClock className="h-[18px] w-[18px]" />
                {t("Gia hạn")} · {formatMoneyVN(total)}đ
              </button>
              {account && (
                <p className="m-0 text-center text-[13px] text-muted">
                  {t("Số dư còn lại sau gia hạn")}: <b className="text-fg">{formatMoneyVN(remain)} VND</b>
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default ExtendCloudModal;
